import { create_requestor_application_journal, create_requestor_application_note } from '@/services/requestor_portal_service';
import { normalize_text, path_value, today_iso_date } from './view_case_details_helpers';
import { format_file_size } from './view_case_details_record_document_helpers';
import {
  file_extension,
  file_key,
  time_entry_attachment,
  time_entry_evidence_required,
  time_entry_file_validation_message,
} from './view_case_details_time_entry_evidence_helpers';

export {
  time_entry_evidence_accept,
  time_entry_evidence_required,
  time_entry_max_file_size_label,
  time_entry_supported_formats_label,
} from './view_case_details_time_entry_evidence_helpers';

export default {
  handle_time_entry_evidence_change(event) {
    add_time_entry_evidence_files(this, Array.from(event?.target?.files || []));
    sync_time_entry_evidence_input(this);
  },
  remove_time_entry_evidence(index) {
    this.new_time_evidence_files = (this.new_time_evidence_files || []).filter((_, file_index) => file_index !== index);
    sync_time_entry_evidence_input(this);
  },
  time_entry_attachment_name(attachment = {}) {
    return attachment?.name || attachment?.file?.name || 'Evidence';
  },
  time_entry_attachment_file_type(attachment = {}) {
    return attachment?.file_type || file_extension(attachment?.file || attachment);
  },
  time_entry_attachment_file_size(attachment = {}) {
    return attachment?.size_label || format_file_size(attachment?.size || attachment?.file?.size);
  },
  time_entry_attachment_status_label(attachment = {}) {
    return attachment?.status || 'Ready to upload';
  },
  async submit_time_entry() {
    const payload = time_entry_payload(this);
    const validation_error = time_entry_validation_error(this, payload);
    if (validation_error) {
      this.time_entries_error = validation_error;
      return;
    }

    await save_time_entry(this, payload);
  },
  async submit_note() {
    const payload = note_payload(this);
    const validation_error = note_validation_error(this, payload);
    if (validation_error) {
      this.notes_error = validation_error;
      return;
    }

    await save_note(this, payload);
  },
  insert_note_mention() {
    insert_note_mention(this);
  },
  handle_note_body_input(event) {
    handle_note_body_input(this, event);
  },
  handle_note_body_cursor_change(event) {
    update_inline_mention_state(this, event?.target);
  },
  handle_note_body_keydown(event) {
    handle_note_body_keydown(this, event);
  },
  select_inline_note_mention(person) {
    select_inline_note_mention(this, person);
  },
  close_note_mention_menu() {
    close_note_mention_menu(this);
  },
  start_note_reply(note) {
    start_note_reply(this, note);
  },
  cancel_note_reply() {
    cancel_note_reply(this);
  },
  submit_note_reply(note) {
    return submit_note_reply(this, note);
  },
};

function time_entry_payload(component) {
  const evidence_items = Array.isArray(component.new_time_evidence_files) ? component.new_time_evidence_files : [];
  const evidence_files = evidence_items
    .map((item) => item?.file || item)
    .filter((file) => typeof File === 'undefined' || file instanceof File);

  return {
    activity: normalize_text(component.new_time_activity),
    hours: Number(component.new_time_hours),
    user_id: Number(component.$store?.getters?.currentUserId || 0),
    evidence_files,
  };
}

function note_payload(component) {
  return {
    body: normalize_text(component.new_note_body),
    user_id: Number(component.$store?.getters?.currentUserId || 0),
  };
}

function time_entry_validation_error(component, payload) {
  clear_time_entry_messages(component);
  return first_present([
    missing_activity_message(payload),
    invalid_hours_message(payload),
    missing_time_case_message(component),
    missing_user_message(payload, 'time entry'),
    missing_required_evidence_message(payload),
  ]);
}

function note_validation_error(component, payload) {
  clear_note_messages(component);
  return first_present([
    missing_note_message(payload),
    missing_note_case_message(component),
    missing_user_message(payload, 'note'),
  ]);
}

function clear_time_entry_messages(component) {
  component.time_entries_error = '';
  component.time_entries_success = '';
}

function clear_note_messages(component) {
  component.notes_error = '';
  component.notes_success = '';
}

function valid_hours(hours) {
  return Number.isFinite(hours) && hours > 0;
}

function missing_activity_message(payload) {
  return payload.activity ? '' : 'Enter an activity before logging time.';
}

function invalid_hours_message(payload) {
  return valid_hours(payload.hours) ? '' : 'Enter valid hours greater than 0.';
}

function missing_time_case_message(component) {
  return component.selected_application_id ? '' : 'No case reference was found for this time entry.';
}

function missing_note_message(payload) {
  return payload.body ? '' : 'Enter a note before posting.';
}

function missing_note_case_message(component) {
  return component.selected_application_id ? '' : 'No case reference was found for this note.';
}

function missing_user_message(payload, label) {
  return payload.user_id ? '' : `No current user was found for this ${label}.`;
}

function missing_required_evidence_message(payload) {
  return !time_entry_evidence_required() || payload.evidence_files.length
    ? ''
    : 'Supporting evidence is mandatory for this time entry. Upload at least one attachment before saving.';
}

async function save_time_entry(component, payload) {
  component.submitting_time_entry = true;
  try {
    await create_requestor_application_journal(time_entry_request(component, payload));
    reset_time_entry_form(component);
    await component.load_time_journals();
  } catch (error) {
    component.time_entries_error = request_error_message(error, 'Failed to log time journal.');
  } finally {
    component.submitting_time_entry = false;
  }
}

function time_entry_request(component, payload) {
  const request = {};
  request.application_id = component.selected_application_id;
  request.user_id = payload.user_id;
  request.journal_description = `${billing_label(component)} - ${payload.activity}`;
  request.hours_spent = payload.hours.toFixed(2);
  request.journal_date = today_iso_date();
  request.evidence_files = payload.evidence_files;
  request.evidence_names = payload.evidence_files.map((file) => file?.name || 'Evidence');
  return request;
}

function add_time_entry_evidence_files(component, files = []) {
  const existing_items = Array.isArray(component.new_time_evidence_files) ? component.new_time_evidence_files : [];
  const seen_keys = new Set(existing_items.map((item) => file_key(item?.file || item)));
  const accepted_items = [];
  const rejected_messages = [];

  files.forEach((file) => {
    const validation_message = time_entry_file_validation_message(file, seen_keys);
    if (validation_message) {
      rejected_messages.push(validation_message);
      return;
    }

    seen_keys.add(file_key(file));
    accepted_items.push(time_entry_attachment(file));
  });

  if (accepted_items.length) {
    component.new_time_evidence_files = [...existing_items, ...accepted_items];
    component.time_entries_success = '';
  }

  if (rejected_messages.length) {
    component.time_entries_error = rejected_messages.join(' ');
  } else if (accepted_items.length) {
    component.time_entries_error = '';
  }
}

function billing_label(component) {
  return component.new_time_billable ? 'Billable' : 'Not Billable';
}

function reset_time_entry_form(component) {
  component.new_time_activity = '';
  component.new_time_hours = '0.5';
  component.new_time_billable = false;
  component.new_time_evidence_files = [];
  sync_time_entry_evidence_input(component);
  component.time_entries_success = 'Time journal logged successfully.';
}

function sync_time_entry_evidence_input(component) {
  if (component.$refs?.time_entry_evidence_input) {
    component.$refs.time_entry_evidence_input.value = '';
  }
}

async function save_note(component, payload) {
  component.submitting_note = true;
  try {
    await create_requestor_application_note(note_request(component, payload));
    component.new_note_body = '';
    component.selected_note_mention_key = '';
    close_note_mention_menu(component);
    component.notes_success = 'Note posted successfully.';
    await component.load_notes();
  } catch (error) {
    component.notes_error = request_error_message(error, 'Failed to post note.');
  } finally {
    component.submitting_note = false;
  }
}

function note_request(component, payload) {
  const request = {};
  request.application_id = component.selected_application_id;
  request.user_id = payload.user_id;
  request.journal_description = payload.body;
  request.hours_spent = '0.00';
  request.journal_date = today_iso_date();
  return request;
}

function note_reply_request(component, note = {}) {
  const user_id = Number(component.$store?.getters?.currentUserId || 0);
  const body = normalize_text(component.note_reply_body);
  const author = normalize_text(note.author);
  const reply_header = author ? `[ReplyTo:${author}] ` : '';
  const request = {};
  request.application_id = component.selected_application_id;
  request.user_id = user_id;
  request.journal_description = `${reply_header}${author ? `@${author} ` : ''}${body}`;
  request.hours_spent = '0.00';
  request.journal_date = today_iso_date();
  return request;
}

function note_reply_validation_error(component, request) {
  clear_note_messages(component);
  return first_present([
    request.journal_description ? '' : 'Enter a reply before posting.',
    component.selected_application_id ? '' : 'No case reference was found for this reply.',
    request.user_id ? '' : 'No current user was found for this reply.',
  ]);
}

function start_note_reply(component, note = {}) {
  component.replying_note_id = note?.id || `${note?.author || ''}-${note?.timestamp || ''}`;
  component.note_reply_body = '';
  component.notes_error = '';
  component.notes_success = '';
}

function cancel_note_reply(component) {
  component.replying_note_id = '';
  component.note_reply_body = '';
}

async function submit_note_reply(component, note = {}) {
  const request = note_reply_request(component, note);
  const validation_error = note_reply_validation_error(component, request);

  if (validation_error) {
    component.notes_error = validation_error;
    return;
  }

  await save_note_reply(component, request);
}

async function save_note_reply(component, request) {
  component.submitting_note_reply = true;
  try {
    await create_requestor_application_note(request);
    cancel_note_reply(component);
    component.notes_success = 'Reply posted successfully.';
    await component.load_notes();
  } catch (error) {
    component.notes_error = request_error_message(error, 'Failed to post reply.');
  } finally {
    component.submitting_note_reply = false;
  }
}

function request_error_message(error, fallback) {
  return first_present([
    error?.response?.data?.message,
    error?.response?.data?.detail,
    path_value(error, ['response', 'data', 'errors', 'journal_description', 0]),
    error?.message,
    fallback,
  ]);
}

function first_present(values = []) {
  return values.find(Boolean) || '';
}

function insert_note_mention(component) {
  const selected_key = String(component.selected_note_mention_key || '');
  const option = (component.note_mention_options || []).find((item) => String(item.key) === selected_key);
  if (!option?.name) {
    return;
  }

  insert_note_mention_option(component, option);
  component.selected_note_mention_key = '';
}

function handle_note_body_input(component, event) {
  if (event?.target) {
    component.new_note_body = event.target.value;
  }

  update_inline_mention_state(component, event?.target);
}

function handle_note_body_keydown(component, event) {
  if (!component.note_mention_menu_open) {
    return;
  }

  if (event.key === 'Escape') {
    event.preventDefault();
    close_note_mention_menu(component);
    return;
  }

  if (['Enter', 'Tab'].includes(event.key)) {
    const first_option = component.filtered_note_mention_options?.[0];
    if (!first_option) {
      return;
    }

    event.preventDefault();
    select_inline_note_mention(component, first_option);
  }
}

function select_inline_note_mention(component, option) {
  if (!option?.name) {
    return;
  }

  const textarea = component.$refs?.note_body_input;
  const current_body = String(component.new_note_body || '');
  const cursor_position = typeof textarea?.selectionStart === 'number'
    ? textarea.selectionStart
    : current_body.length;
  const start_index = Number(component.note_mention_start_index);
  const range = Number.isFinite(start_index) && start_index >= 0
    ? { start: start_index, end: cursor_position }
    : null;

  insert_note_mention_option(component, option, range);
}

function update_inline_mention_state(component, textarea = null) {
  const input = textarea || component.$refs?.note_body_input;
  const body = String(component.new_note_body || '');
  const cursor_position = typeof input?.selectionStart === 'number'
    ? input.selectionStart
    : body.length;
  const before_cursor = body.slice(0, cursor_position);
  const mention_match = /(^|\s)@([A-Za-z0-9._'-]*)$/.exec(before_cursor);

  if (!mention_match || !component.note_mention_options?.length) {
    close_note_mention_menu(component);
    return;
  }

  component.note_mention_start_index = mention_match.index + mention_match[1].length;
  component.note_mention_query = mention_match[2] || '';
  component.note_mention_menu_open = true;
}

function close_note_mention_menu(component) {
  component.note_mention_menu_open = false;
  component.note_mention_query = '';
  component.note_mention_start_index = -1;
}

function insert_note_mention_option(component, option, range = null) {
  const mention = `@${option.name}`;
  const textarea = component.$refs?.note_body_input;
  const current_body = String(component.new_note_body || '');
  const cursor_position = typeof textarea?.selectionStart === 'number'
    ? textarea.selectionStart
    : current_body.length;
  const start_position = range?.start ?? cursor_position;
  const end_position = range?.end ?? cursor_position;
  const before = current_body.slice(0, start_position);
  const after = current_body.slice(end_position);
  const prefix = before && !/\s$/.test(before) ? ' ' : '';
  const suffix = after && !/^\s/.test(after) ? ' ' : '';
  const next_body = `${before}${prefix}${mention} ${suffix}${after}`.replace(/\s{3,}/g, '  ');

  component.new_note_body = next_body;
  close_note_mention_menu(component);

  component.$nextTick(() => {
    if (textarea?.focus) {
      textarea.focus();
    }

    const next_cursor_position = before.length + prefix.length + mention.length + 1;
    if (textarea?.setSelectionRange) {
      textarea.setSelectionRange(next_cursor_position, next_cursor_position);
    }
  });
}
